import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { Link } from "react-router-dom";
import { MessageSquare, MessageSquarePlus } from "lucide-react";
import { messagesApi } from "@/api/messaging";
import type { ConversationListItemDto, MessageAttachmentDto } from "@/api/messaging";
import { useAuth } from "@/hooks/useAuth";
import { relativeTime } from "@/components/ux/relativeTime";
import { Button } from "@/design-system/components/ui";
import { NewMessageModal } from "@/components/messaging/NewMessageModal";
import { cn } from "@/design-system/lib/utils";

/**
 * Internal Messaging Integration Standard v1.1 §5 — every record detail
 * page can drop this component on its Messages tab. Lists every
 * conversation that ever attached <c>(resourceType, resourceId)</c>.
 *
 * v1.1 addition: when <c>entityLabel</c> is provided, the panel also
 * exposes a "+ Compose message about this {entity}" affordance that opens
 * the central composer with the entity pre-attached as a chip.
 *
 * Visibility:
 *   - Participant or compliance reader (E4 / B5) — panel renders.
 *   - Everyone else with zero related threads — panel still renders so the
 *     compose affordance is reachable, but the list shows the empty hint.
 *   - E4/B5 always see every related thread regardless of participation,
 *     plus a "Compliance view" pill so they understand why.
 */
export function RelatedConversationsPanel({
  resourceType,
  resourceId,
  entityLabel,
}: {
  resourceType: string;
  resourceId: string;
  /**
   * Human-readable label for the host record (e.g. "Smith Acquisition",
   * "Acme Brokerage"). Required to render the compose affordance — without
   * it the optimistic chip would have nothing to display before the server
   * re-resolves the label on send.
   */
  entityLabel?: string;
}) {
  const { user } = useAuth();
  const isComplianceReader = !!user?.permissions.includes("Messaging.ViewAll");
  const [composerOpen, setComposerOpen] = useState(false);

  const related = useQuery({
    queryKey: ["messaging", "related", resourceType, resourceId],
    queryFn: () => messagesApi.related(resourceType, resourceId),
    enabled: !!resourceType && !!resourceId,
  });

  const items = related.data ?? [];
  const showCompose = !!entityLabel;

  // Hide entirely only when there's no compose affordance to reach AND no
  // results to surface AND the viewer isn't a compliance reader.
  if (!showCompose && !isComplianceReader && items.length === 0) return null;

  const prefillAttachment: MessageAttachmentDto | undefined = entityLabel
    ? {
        resourceType,
        resourceId,
        displayLabel: prettyTypeLabel(resourceType, entityLabel),
        detailUrl: "",
        isDeleted: false,
        viewerCanAccess: true,
      }
    : undefined;

  return (
    <Panel
      isComplianceReader={isComplianceReader}
      composeAction={
        showCompose ? (
          <Button
            size="sm"
            variant="secondary"
            onClick={() => setComposerOpen(true)}
          >
            <MessageSquarePlus size={14} strokeWidth={1.5} aria-hidden />
            Compose message about this {entityNoun(resourceType)}
          </Button>
        ) : null
      }
    >
      {related.isLoading ? (
        <div className="text-small text-gray-mid">Loading…</div>
      ) : items.length === 0 ? (
        <div className="text-small text-gray-mid">No conversations have referenced this record.</div>
      ) : (
        <ul className="m-0 p-0 list-none divide-y divide-border-light">
          {items.map((c) => <RelatedRow key={c.id} c={c} />)}
        </ul>
      )}

      {prefillAttachment && (
        <NewMessageModal
          open={composerOpen}
          onClose={() => setComposerOpen(false)}
          prefillAttachment={prefillAttachment}
        />
      )}
    </Panel>
  );
}

function Panel({
  children,
  isComplianceReader,
  composeAction,
}: {
  children: React.ReactNode;
  isComplianceReader?: boolean;
  composeAction?: React.ReactNode;
}) {
  return (
    <section className="bg-white-pure border border-border-light rounded-lg p-space-3">
      <div className="flex items-center justify-between gap-space-2 mb-space-2 flex-wrap">
        <h3 className="font-display font-bold text-small uppercase tracking-wider text-gold-shadow flex items-center gap-space-1 m-0">
          <MessageSquare size={14} strokeWidth={1.5} aria-hidden />
          Related Conversations
        </h3>
        <div className="flex items-center gap-space-2">
          {isComplianceReader && (
            <span className="text-label uppercase tracking-label text-gold-shadow border border-gold/40 rounded-pill px-space-2 py-[1px]">
              Compliance view
            </span>
          )}
          {composeAction}
        </div>
      </div>
      {children}
    </section>
  );
}

function RelatedRow({ c }: { c: ConversationListItemDto }) {
  return (
    <li>
      <Link
        to={`/messages/${c.id}`}
        className={cn(
          "block px-space-2 py-space-2 rounded-md no-underline text-inherit",
          "hover:bg-white-warm transition-colors",
        )}
      >
        <div className="flex items-center justify-between gap-space-2">
          <span className="font-display font-semibold text-small text-onyx truncate">
            {c.otherUserDisplayName}
          </span>
          {c.lastMessageAt && (
            <span className="text-label text-gray-mid shrink-0">
              {relativeTime(c.lastMessageAt)}
            </span>
          )}
        </div>
        {c.lastMessagePreview && (
          <div className="text-label text-gray-mid truncate">{c.lastMessagePreview}</div>
        )}
      </Link>
    </li>
  );
}

// User-facing noun used in the compose CTA and chip label. The lowercase
// resourceType (per §6.1) is the key; the values here are the words a user
// reads in the UI.
const TYPE_NOUNS: Record<string, string> = {
  deal: "Deal",
  loan: "Loan",
  broker: "Broker",
  brokerage: "Brokerage",
  person: "person",
  entity: "entity",
  business: "business",
  vendor: "Vendor",
  spv: "SPV",
  spv_pool: "SPV Pool",
  investor: "Investor",
  candidate: "Candidate",
  lead: "Lead",
  indenture_trustee: "Indenture Trustee",
  trust_company: "Trust Company",
  placement_agent: "Placement Agent",
  backup_servicer: "Backup Servicer",
  tps: "TPS",
  local_enforcer: "Local Enforcer",
  portal_user: "Portal Account",
  "reunderwriting-case": "Reunderwriting Case",
  borrower: "Borrower",
  seller: "Seller",
  // Buyer CRM — the compose CTA reads "Message about this buyer". Distinct
  // from `lead` (BrokerLead) and from the borrower-side `buyer` Party token.
  buyer_lead: "Buyer",
  // Buyer programme — without an entry the compose CTA reads "Message about
  // this record", which is the silent half-registration this map exists to
  // prevent. Nouns match the server's chip prefixes in ResourceLabelService.
  listing: "Listing",
  buyer_submission: "Submission",
};

function entityNoun(resourceType: string): string {
  return TYPE_NOUNS[resourceType] ?? "record";
}

function prettyTypeLabel(resourceType: string, label: string): string {
  const noun = TYPE_NOUNS[resourceType];
  // ResourceLabelService composes labels as "{TypeLabel} — {Headline}" for
  // most types; mirror that format optimistically so the chip looks the same
  // before the server re-resolves it on send.
  const display = noun
    ? `${capitalize(noun)} — ${label}`
    : label;
  return display;
}

function capitalize(s: string): string {
  return s.length === 0 ? s : s[0].toUpperCase() + s.slice(1);
}
